import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import { toast } from 'react-toastify';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import ConfirmationModal from '../../components/ConfirmationModal';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { Redirect } from 'react-router-dom';

const FIELD_KINDS = [
  { value: 'TEXT', labelKey: 'customContactFieldsPage.fieldKinds.text' },
  { value: 'NUMBER', labelKey: 'customContactFieldsPage.fieldKinds.number' },
  { value: 'SELECT_SINGLE', labelKey: 'customContactFieldsPage.fieldKinds.selectSingle' },
  { value: 'SELECT_MULTI', labelKey: 'customContactFieldsPage.fieldKinds.selectMulti' },
  { value: 'SELECT_MULTI_LIMITED', labelKey: 'customContactFieldsPage.fieldKinds.selectMultiLimited' },
];

const LEGACY_PARAMS = ['SITUACAO', 'CANAL', 'REGIAO'];

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: 'auto',
    ...theme.scrollbarStyles,
  },
  subtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  groupToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
}));

const CustomContactFields = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    param: '',
    label: '',
    fieldKind: 'SELECT_SINGLE',
    multiMax: '',
    sortOrder: '0',
  });

  const [valueDialogOpen, setValueDialogOpen] = useState(false);
  const [editingValueId, setEditingValueId] = useState(null);
  const [formValue, setFormValue] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingValueId, setDeletingValueId] = useState(null);
  const [confirmGroupOpen, setConfirmGroupOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [legacyFallback, setLegacyFallback] = useState(false);

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.id - b.id),
    [groups]
  );

  const activeGroup = sortedGroups[tab] || null;

  const loadAll = useCallback(async () => {
    setLoading(true);
    let gList = [];
    let fromFallback = false;
    try {
      const { data } = await api.get('/contact-field-groups');
      gList = Array.isArray(data) ? data : [];
    } catch (err) {
      fromFallback = true;
      toast.warning(i18n.t('customContactFieldsPage.groupsApiFallback'));
      gList = [
        {
          id: -1,
          companyId: user?.companyId,
          param: 'SITUACAO',
          label: i18n.t('customContactFieldsPage.tabSituacao'),
          fieldKind: 'SELECT_SINGLE',
          multiMax: null,
          sortOrder: 0,
        },
        {
          id: -2,
          companyId: user?.companyId,
          param: 'CANAL',
          label: i18n.t('customContactFieldsPage.tabCanal'),
          fieldKind: 'SELECT_SINGLE',
          multiMax: null,
          sortOrder: 1,
        },
        {
          id: -3,
          companyId: user?.companyId,
          param: 'REGIAO',
          label: i18n.t('customContactFieldsPage.tabRegiao'),
          fieldKind: 'SELECT_SINGLE',
          multiMax: null,
          sortOrder: 2,
        },
      ];
    }
    let rowsData = [];
    try {
      const { data } = await api.get('/custom-attributes/manage');
      rowsData = Array.isArray(data) ? data : [];
    } catch (err) {
      toastError(err);
    }
    setLegacyFallback(fromFallback);
    setGroups(gList);
    setRows(rowsData);
    setLoading(false);
  }, [user?.companyId]);

  useEffect(() => {
    if (user?.profile === 'admin') {
      loadAll();
    }
  }, [user?.profile, loadAll]);

  useEffect(() => {
    if (tab >= sortedGroups.length) {
      setTab(Math.max(0, sortedGroups.length - 1));
    }
  }, [sortedGroups.length, tab]);

  if (user?.profile !== 'admin') {
    return <Redirect to="/" />;
  }

  const filteredValues = activeGroup
    ? rows.filter(r => r.param === activeGroup.param)
    : [];

  const isSelectKind = kind =>
    kind === 'SELECT_SINGLE' || kind === 'SELECT_MULTI' || kind === 'SELECT_MULTI_LIMITED';

  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({
      param: '',
      label: '',
      fieldKind: 'SELECT_SINGLE',
      multiMax: '',
      sortOrder: String(sortedGroups.length),
    });
    setGroupDialogOpen(true);
  };

  const openEditGroup = g => {
    if (g && g.id < 0) {
      toast.warning(i18n.t('customContactFieldsPage.cannotEditFallbackGroup'));
      return;
    }
    setEditingGroup(g);
    setGroupForm({
      param: g.param,
      label: g.label || '',
      fieldKind: g.fieldKind || 'SELECT_SINGLE',
      multiMax: g.multiMax != null ? String(g.multiMax) : '',
      sortOrder: String(g.sortOrder ?? 0),
    });
    setGroupDialogOpen(true);
  };

  const saveGroup = async () => {
    const label = String(groupForm.label || '').trim();
    if (!label) {
      toast.error(i18n.t('customContactFieldsPage.groupLabelRequired'));
      return;
    }
    const payload = {
      label,
      fieldKind: groupForm.fieldKind,
      sortOrder: parseInt(String(groupForm.sortOrder).replace(/\D/g, ''), 10) || 0,
    };
    if (groupForm.fieldKind === 'SELECT_MULTI_LIMITED') {
      const m = parseInt(String(groupForm.multiMax).replace(/\D/g, ''), 10);
      if (!m || m < 1) {
        toast.error(i18n.t('customContactFieldsPage.multiMaxRequired'));
        return;
      }
      payload.multiMax = m;
    }
    try {
      if (editingGroup) {
        await api.put(`/contact-field-groups/${editingGroup.id}`, payload);
        toast.success(i18n.t('customContactFieldsPage.groupUpdateSuccess'));
      } else {
        const param = String(groupForm.param || '')
          .trim()
          .toUpperCase()
          .replace(/\s+/g, '_');
        if (!param || param.length < 2) {
          toast.error(i18n.t('customContactFieldsPage.groupParamRequired'));
          return;
        }
        await api.post('/contact-field-groups', { ...payload, param });
        toast.success(i18n.t('customContactFieldsPage.groupCreateSuccess'));
      }
      setGroupDialogOpen(false);
      await loadAll();
    } catch (err) {
      toastError(err);
    }
  };

  const confirmDeleteGroup = g => {
    if (LEGACY_PARAMS.includes(g.param)) {
      toast.error(i18n.t('customContactFieldsPage.cannotDeleteLegacyGroup'));
      return;
    }
    setDeletingGroup(g);
    setConfirmGroupOpen(true);
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      await api.delete(`/contact-field-groups/${deletingGroup.id}`);
      toast.success(i18n.t('customContactFieldsPage.groupDeleteSuccess'));
      setConfirmGroupOpen(false);
      setDeletingGroup(null);
      setTab(0);
      await loadAll();
    } catch (err) {
      toastError(err);
    }
  };

  const openCreateValue = () => {
    if (!activeGroup || !isSelectKind(activeGroup.fieldKind)) {
      toast.error(i18n.t('customContactFieldsPage.optionsOnlyForSelect'));
      return;
    }
    setEditingValueId(null);
    setFormValue('');
    setValueDialogOpen(true);
  };

  const openEditValue = row => {
    setEditingValueId(row.id);
    setFormValue(row.value || '');
    setValueDialogOpen(true);
  };

  const handleSaveValue = async () => {
    const v = String(formValue).trim();
    if (!v) {
      toast.error(i18n.t('customContactFieldsPage.valueRequired'));
      return;
    }
    if (!activeGroup) return;
    try {
      if (editingValueId) {
        await api.put(`/custom-attributes/${editingValueId}`, { value: v });
        toast.success(i18n.t('customContactFieldsPage.successUpdate'));
      } else {
        await api.post('/custom-attributes', { param: activeGroup.param, value: v });
        toast.success(i18n.t('customContactFieldsPage.successCreate'));
      }
      setValueDialogOpen(false);
      await loadAll();
    } catch (err) {
      toastError(err);
    }
  };

  const confirmDeleteValue = row => {
    setDeletingValueId(row.id);
    setConfirmOpen(true);
  };

  const handleDeleteValue = async () => {
    if (!deletingValueId) return;
    try {
      await api.delete(`/custom-attributes/${deletingValueId}`);
      toast.success(i18n.t('customContactFieldsPage.successDelete'));
      setConfirmOpen(false);
      setDeletingValueId(null);
      await loadAll();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('customContactFieldsPage.title')}</Title>
        <MainHeaderButtonsWrapper>
          {!legacyFallback && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateGroup}>
              {i18n.t('customContactFieldsPage.newGroup')}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Typography className={classes.subtitle} variant="body2">
        {i18n.t('customContactFieldsPage.subtitleExtended')}
      </Typography>
      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <Typography>{i18n.t('customContactFieldsPage.loading')}</Typography>
        ) : sortedGroups.length === 0 ? (
          <Typography color="textSecondary">{i18n.t('customContactFieldsPage.noGroups')}</Typography>
        ) : (
          <>
            <div className={classes.groupToolbar}>
              <Button
                size="small"
                variant="outlined"
                disabled={!activeGroup || activeGroup.id < 0}
                onClick={() => activeGroup && openEditGroup(activeGroup)}
              >
                {i18n.t('customContactFieldsPage.editGroup')}
              </Button>
              {activeGroup && activeGroup.id > 0 && !LEGACY_PARAMS.includes(activeGroup.param) && (
                <Button size="small" color="secondary" variant="outlined" onClick={() => confirmDeleteGroup(activeGroup)}>
                  {i18n.t('customContactFieldsPage.deleteGroup')}
                </Button>
              )}
            </div>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {sortedGroups.map(g => (
                <Tab
                  key={g.id}
                  label={`${g.label}${g.fieldKind && !isSelectKind(g.fieldKind) ? ` (${i18n.t('customContactFieldsPage.tabFreeInput')})` : ''}`}
                />
              ))}
            </Tabs>
            <div className={classes.tabPanel}>
              {!activeGroup ? null : !isSelectKind(activeGroup.fieldKind) ? (
                <Typography color="textSecondary">
                  {i18n.t('customContactFieldsPage.freeFieldHint')}
                </Typography>
              ) : filteredValues.length === 0 ? (
                <Typography color="textSecondary">{i18n.t('customContactFieldsPage.empty')}</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t('customContactFieldsPage.value')}</TableCell>
                      <TableCell align="right" width={120}>
                        {i18n.t('customContactFieldsPage.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredValues.map(row => (
                      <TableRow key={row.id}>
                        <TableCell>{row.value}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditValue(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => confirmDeleteValue(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {activeGroup && isSelectKind(activeGroup.fieldKind) && (
                <div style={{ marginTop: 16 }}>
                  <Button variant="contained" color="primary" onClick={openCreateValue}>
                    {i18n.t('customContactFieldsPage.add')}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Paper>

      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup
            ? i18n.t('customContactFieldsPage.editGroupTitle')
            : i18n.t('customContactFieldsPage.newGroupTitle')}
        </DialogTitle>
        <DialogContent>
          {!editingGroup && (
            <TextField
              label={i18n.t('customContactFieldsPage.groupParam')}
              value={groupForm.param}
              onChange={e => setGroupForm({ ...groupForm, param: e.target.value })}
              fullWidth
              variant="outlined"
              margin="normal"
              helperText={i18n.t('customContactFieldsPage.groupParamHelp')}
            />
          )}
          <TextField
            label={i18n.t('customContactFieldsPage.groupLabel')}
            value={groupForm.label}
            onChange={e => setGroupForm({ ...groupForm, label: e.target.value })}
            fullWidth
            variant="outlined"
            margin="normal"
            autoFocus={!!editingGroup}
          />
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>{i18n.t('customContactFieldsPage.fieldKind')}</InputLabel>
            <Select
              label={i18n.t('customContactFieldsPage.fieldKind')}
              value={groupForm.fieldKind}
              onChange={e => setGroupForm({ ...groupForm, fieldKind: e.target.value })}
              disabled={!!editingGroup && LEGACY_PARAMS.includes(editingGroup.param)}
            >
              {FIELD_KINDS.map(f => (
                <MenuItem key={f.value} value={f.value}>
                  {i18n.t(f.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {groupForm.fieldKind === 'SELECT_MULTI_LIMITED' && (
            <TextField
              label={i18n.t('customContactFieldsPage.multiMax')}
              type="number"
              value={groupForm.multiMax}
              onChange={e => setGroupForm({ ...groupForm, multiMax: e.target.value })}
              fullWidth
              variant="outlined"
              margin="normal"
              inputProps={{ min: 1, max: 99 }}
            />
          )}
          <TextField
            label={i18n.t('customContactFieldsPage.sortOrder')}
            type="number"
            value={groupForm.sortOrder}
            onChange={e => setGroupForm({ ...groupForm, sortOrder: e.target.value })}
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)} color="secondary" variant="outlined">
            {i18n.t('customContactFieldsPage.cancel')}
          </Button>
          <Button onClick={saveGroup} color="primary" variant="contained">
            {i18n.t('customContactFieldsPage.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={valueDialogOpen} onClose={() => setValueDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingValueId ? i18n.t('customContactFieldsPage.editTitle') : i18n.t('customContactFieldsPage.addTitle')}
          {activeGroup ? ` — ${activeGroup.label}` : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={i18n.t('customContactFieldsPage.value')}
            value={formValue}
            onChange={e => setFormValue(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValueDialogOpen(false)} color="secondary" variant="outlined">
            {i18n.t('customContactFieldsPage.cancel')}
          </Button>
          <Button onClick={handleSaveValue} color="primary" variant="contained">
            {i18n.t('customContactFieldsPage.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={i18n.t('customContactFieldsPage.confirmDeleteTitle')}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteValue}
      >
        {i18n.t('customContactFieldsPage.confirmDelete')}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t('customContactFieldsPage.confirmDeleteGroupTitle')}
        open={confirmGroupOpen}
        onClose={() => setConfirmGroupOpen(false)}
        onConfirm={handleDeleteGroup}
      >
        {i18n.t('customContactFieldsPage.confirmDeleteGroup')}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default CustomContactFields;
